import { useTranslation } from 'react-i18next';
import { ActionsColumn as ActionsColumnCommon, ActionType } from '@otp/ui';
import { Archive, Clone, Configure } from '@otp/icons';
import {
  WttpQuestion,
  WttpQuestionStatus,
} from 'src/graphql/generated/graphql';
import { useAuthContext } from 'src/context/auth.context';
import styles from './index.module.css';
import React from 'react';
import { ActionModals, WttpQuestionAction } from './actions-modals';

type ActionsColumProps = {
  wttpQuestion: Pick<
    WttpQuestion,
    | 'id'
    | 'parentId'
    | 'organizationId'
    | 'status'
    | 'isActive'
    | 'usedByQuestionnairesCount'
  >;
  onActionFinished?: (action: WttpQuestionAction) => void;
};

export const ActionsColumn = ({
  wttpQuestion,
  onActionFinished,
}: ActionsColumProps) => {
  const { t } = useTranslation();
  const { isAdmin, isOtpPro, user } = useAuthContext();
  const [action, setAction] = React.useState<WttpQuestionAction>();

  const userOrganizationId = user?.organizationId;
  const isUserOrganizationQuestion =
    userOrganizationId === wttpQuestion?.organizationId;
  const canClone = isOtpPro && !isUserOrganizationQuestion;
  const canEdit = isAdmin || (isOtpPro && isUserOrganizationQuestion);
  const canArchive = canEdit;
  const canActivate = canEdit && wttpQuestion.isActive;

  const actions = [
    canClone && {
      id: 'clone',
      label: t('common.clone'),
      icon: <Clone />,
      onClick: () => setAction(WttpQuestionAction.Clone),
    },
    canEdit && {
      id: 'edit',
      label: t('common.edit'),
      icon: <Configure />,
      onClick: () => setAction(WttpQuestionAction.Edit),
    },
    canActivate && {
      id: 'activate',
      label:
        wttpQuestion.status === WttpQuestionStatus.Active
          ? t('common.set-as-draft')
          : t('common.activate'),
      icon: <Archive />,
      onClick: () => {
        const action =
          wttpQuestion.status === WttpQuestionStatus.Active
            ? WttpQuestionAction.Deactivate
            : WttpQuestionAction.Activate;

        if (
          action === WttpQuestionAction.Deactivate &&
          !!wttpQuestion.usedByQuestionnairesCount
        ) {
          return setAction(WttpQuestionAction.ArchiveAlert);
        }

        setAction(action);
      },
    },
    canArchive && {
      id: 'archive',
      label: wttpQuestion.isActive ? t('common.delete') : t('common.restore'),
      icon: <Archive />,
      onClick: () => {
        if (!!wttpQuestion.usedByQuestionnairesCount) {
          return setAction(WttpQuestionAction.ArchiveAlert);
        }

        setAction(
          wttpQuestion.isActive
            ? WttpQuestionAction.Archive
            : WttpQuestionAction.Unarchive,
        );
      },
    },
  ].filter(Boolean) as ActionType[];

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {wttpQuestion.parentId && <div data-role="selected-row" />}
      <ActionsColumnCommon
        actions={actions}
        classes={{ menu: styles.actionsColumn }}
      />
      {action && (
        <ActionModals
          question={wttpQuestion}
          onClose={() => setAction(undefined)}
          action={action}
          onActionFinished={onActionFinished}
        />
      )}
    </div>
  );
};
